import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './SalesItems.css';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/db';
import Modal from '../components/Modal';
import LoadingOverlay from '../components/LoadingOverlay';

export default function SalesItems() {
    const { warehouseId } = useAuth();

    // DB Items state
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (warehouseId) {
            loadItems();
        }
        // eslint-disable-next-line
    }, [warehouseId]);

    async function loadItems(retryCount = 0) {
        setLoading(true);
        try {
            const data = await api.getItems(warehouseId);
            const mappedItems = data.map(doc => ({
                id: doc.$id,
                name: doc.name,
                cost: doc.cost || 0,
                price: doc.sell_price || 0,
                stock: doc.current_stock || 0,
                profitPercentage: doc.profit_percentage || null
            }));
            // Sort items by name alphabetically
            mappedItems.sort((a, b) => a.name.localeCompare(b.name, 'ar'));
            setItems(mappedItems);
            setLoading(false);
        } catch (error) {
            console.error("Failed to load items, retrying...", error);
            // Retry after a delay (keep loading, don't show empty screen)
            const delay = Math.min(3000 * (retryCount + 1), 10000); // 3s, 6s, 9s, max 10s
            setTimeout(() => loadItems(retryCount + 1), delay);
        }
    }

    const [modal, setModal] = useState({ isOpen: false, message: '', type: 'success' });
    const [searchQuery, setSearchQuery] = useState('');

    // Form Modal State
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null); // null means adding new
    const [formData, setFormData] = useState({ name: '', cost: '', price: '', profitPercentage: '' });
    const [pricingMethod, setPricingMethod] = useState('manual'); // 'manual' or 'percentage'

    // Bulk Increase Modal State
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [bulkPercentage, setBulkPercentage] = useState('');
    const [excludedItems, setExcludedItems] = useState(new Set());
    const [isApplyingBulk, setIsApplyingBulk] = useState(false);

    // Calculation helpers
    function calculatePriceFromPercentage(cost, percentage) {
        const c = parseFloat(cost) || 0;
        const p = parseFloat(percentage) || 0;
        return Math.round(c * (1 + p / 100));
    }

    function calculatePercentageFromPrice(cost, price) {
        const c = parseFloat(cost) || 0;
        const pr = parseFloat(price) || 0;
        if (c <= 0) return 0;
        return ((pr - c) / c) * 100;
    }

    async function deleteItem(id) {
        if (window.confirm('هل أنت متأكد من حذف هذا الصنف؟')) {
            setIsProcessing(true);
            try {
                await api.deleteItem(id, warehouseId);
                loadItems(); // Refresh
                setModal({ isOpen: true, message: 'تم حذف الصنف بنجاح', type: 'success' });
            } catch (error) {
                console.error("Delete failed", error);
                setModal({ isOpen: true, message: 'فشل حذف الصنف', type: 'error' });
            } finally {
                setIsProcessing(false);
            }
        }
    }

    function closeModal() {
        setModal({ ...modal, isOpen: false });
    }

    function calculateProfit(cost, price) {
        const c = parseFloat(cost) || 0;
        const p = parseFloat(price) || 0;
        return Math.round(p - c).toLocaleString('en-US');
    }

    const formatNumber = (num) => Math.round(Number(num) || 0).toLocaleString('en-US');

    // --- Form Handlers ---

    function openAddModal() {
        setEditingItem(null);
        setFormData({ name: '', cost: '', price: '', profitPercentage: '' });
        setPricingMethod('manual');
        setIsFormOpen(true);
    }

    function openEditModal(item) {
        setEditingItem(item);
        // Determine pricing method based on existing data
        const hasProfitPercentage = item.profitPercentage !== null && item.profitPercentage !== undefined;
        setPricingMethod(hasProfitPercentage ? 'percentage' : 'manual');
        setFormData({
            name: item.name,
            cost: item.cost,
            price: item.price,
            profitPercentage: hasProfitPercentage ? item.profitPercentage : ''
        });
        setIsFormOpen(true);
    }

    function handleFormChange(e) {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };

            // Auto-calculate based on pricing method
            if (pricingMethod === 'percentage') {
                if (name === 'cost' || name === 'profitPercentage') {
                    updated.price = calculatePriceFromPercentage(updated.cost, updated.profitPercentage);
                }
            } else {
                // Manual mode: auto-calculate percentage in background
                if (name === 'cost' || name === 'price') {
                    updated.profitPercentage = calculatePercentageFromPrice(updated.cost, updated.price);
                }
            }

            return updated;
        });
    }

    function handlePricingMethodChange(method) {
        setPricingMethod(method);
        // Recalculate based on new method
        if (method === 'percentage' && formData.cost) {
            // Keep existing percentage or calculate from current price
            const pct = formData.profitPercentage || calculatePercentageFromPrice(formData.cost, formData.price);
            setFormData(prev => ({
                ...prev,
                profitPercentage: pct,
                price: calculatePriceFromPercentage(prev.cost, pct)
            }));
        } else if (method === 'manual' && formData.cost && formData.price) {
            // Calculate percentage from price
            setFormData(prev => ({
                ...prev,
                profitPercentage: calculatePercentageFromPrice(prev.cost, prev.price)
            }));
        }
    }

    async function saveItem() {
        if (!formData.name) {
            alert('يرجى إدخال اسم الصنف');
            return;
        }

        // Normalize name by removing all spaces for duplicate check
        const normalizeName = (name) => name.replace(/\s+/g, '').toLowerCase();
        const normalizedNewName = normalizeName(formData.name);

        // Check for duplicates (exclude current item if editing)
        const isDuplicate = items.some(item => {
            const normalizedExisting = normalizeName(item.name);
            // If editing, exclude the current item from check
            if (editingItem && item.id === editingItem.id) {
                return false;
            }
            return normalizedExisting === normalizedNewName;
        });

        if (isDuplicate) {
            setModal({ isOpen: true, message: 'هذا الصنف موجود بالفعل! لا يمكن إضافة صنف مكرر.', type: 'error' });
            return;
        }

        // Calculate final values (ensure integer price)
        const finalPrice = Math.round(parseFloat(formData.price) || 0);
        const finalPercentage = pricingMethod === 'percentage'
            ? parseFloat(formData.profitPercentage) || 0
            : calculatePercentageFromPrice(formData.cost, finalPrice);

        setIsProcessing(true);
        try {
            if (editingItem) {
                // Update existing
                await api.updateItem(editingItem.id, {
                    name: formData.name,
                    cost: parseFloat(formData.cost) || 0,
                    sell_price: finalPrice,
                    profit_percentage: finalPercentage
                }, warehouseId);
                setModal({ isOpen: true, message: 'تم تعديل الصنف بنجاح', type: 'success' });
            } else {
                // Add new
                await api.addItem({
                    name: formData.name,
                    category: 'Sales',
                    cost: parseFloat(formData.cost) || 0,
                    sell_price: finalPrice,
                    profit_percentage: finalPercentage,
                    current_stock: 0
                }, warehouseId);
                setModal({ isOpen: true, message: 'تم إضافة الصنف بنجاح', type: 'success' });
            }
            loadItems();
            setIsFormOpen(false);
        } catch (error) {
            console.error("Save failed", error);
            setModal({ isOpen: true, message: 'فشل حفظ الصنف', type: 'error' });
        } finally {
            setIsProcessing(false);
        }
    }

    // Bulk Increase Handlers
    function openBulkModal() {
        setBulkPercentage('');
        setExcludedItems(new Set());
        setIsBulkModalOpen(true);
    }

    function toggleExcludeItem(itemId) {
        setExcludedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    }

    async function applyBulkIncrease() {
        const pct = parseFloat(bulkPercentage);
        if (isNaN(pct) || pct === 0) {
            setModal({ isOpen: true, message: 'يرجى إدخال نسبة صحيحة', type: 'error' });
            return;
        }

        setIsApplyingBulk(true);
        try {
            const result = await api.bulkIncreaseItemPrices(items, pct, Array.from(excludedItems));
            setModal({
                isOpen: true,
                message: `تم تحديث ${result.updatedCount} صنف بنجاح`,
                type: 'success'
            });
            setIsBulkModalOpen(false);
            loadItems(); // Refresh
        } catch (error) {
            console.error('Bulk increase failed:', error);
            setModal({ isOpen: true, message: 'خطاء في الشبكة ، يرجى المحاولة مرة أخرى  ', type: 'error' });
        } finally {
            setIsApplyingBulk(false);
        }
    }

    return (
        <div className="sales-items-root">
            <LoadingOverlay isLoading={isProcessing || loading} />
            <Modal
                isOpen={modal.isOpen}
                message={modal.message}
                type={modal.type}
                onClose={closeModal}
            />

            {/* Form Modal */}
            {isFormOpen && (
                <div className="form-modal-overlay">
                    <div className="form-modal-content">
                        <h2>{editingItem ? 'تعديل الصنف' : 'إضافة صنف جديد'}</h2>

                        <div className="modal-form-group">
                            <label>اسم الصنف</label>
                            <input name="name" value={formData.name} onChange={handleFormChange} placeholder="اسم الصنف" />
                        </div>

                        <div className="modal-form-group">
                            <label>التكلفة</label>
                            <input type="number" name="cost" value={formData.cost} onChange={handleFormChange} placeholder="0" />
                        </div>

                        <div className="modal-form-group">
                            <label>طريقة تحديد السعر</label>
                            <div className="pricing-method-toggle">
                                <label className={`method-option ${pricingMethod === 'manual' ? 'active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="pricingMethod"
                                        value="manual"
                                        checked={pricingMethod === 'manual'}
                                        onChange={() => handlePricingMethodChange('manual')}
                                    />
                                    إدخال يدويا
                                </label>
                                <label className={`method-option ${pricingMethod === 'percentage' ? 'active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="pricingMethod"
                                        value="percentage"
                                        checked={pricingMethod === 'percentage'}
                                        onChange={() => handlePricingMethodChange('percentage')}
                                    />
                                    نسبة ربح %
                                </label>
                            </div>
                        </div>

                        {pricingMethod === 'percentage' ? (
                            <>
                                <div className="modal-form-group">
                                    <label>نسبة الربح (%)</label>
                                    <input
                                        type="number"
                                        name="profitPercentage"
                                        value={formData.profitPercentage}
                                        onChange={handleFormChange}
                                        placeholder="0"
                                    />
                                </div>
                                <div className="modal-form-group">
                                    <label>سعر البيع (محسوب تلقائياً)</label>
                                    <input
                                        type="number"
                                        value={formData.price}
                                        readOnly
                                        style={{ backgroundColor: '#e9ecef' }}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="modal-form-group">
                                <label>سعر البيع</label>
                                <input type="number" name="price" value={formData.price} onChange={handleFormChange} placeholder="0" />
                            </div>
                        )}

                        <div className="modal-actions">
                            <button className="save-btn" onClick={saveItem}>حفظ</button>
                            <button className="cancel-btn" onClick={() => setIsFormOpen(false)}>إلغاء</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Increase Modal */}
            {isBulkModalOpen && (
                <div className="form-modal-overlay">
                    <div className="form-modal-content bulk-modal">
                        <h2>زيادة عامة للأسعار</h2>

                        <div className="modal-form-group">
                            <label>نسبة الزيادة (%)</label>
                            <input
                                type="number"
                                value={bulkPercentage}
                                onChange={(e) => setBulkPercentage(e.target.value)}
                                placeholder="مثال: 10 لزيادة 10%"
                            />
                        </div>

                        <div className="exclusion-section">
                            <h3>إستثناء أصناف</h3>
                            <p className="exclusion-hint">اختر الأصناف التي لن تتأثر بالزيادة:</p>
                            <div className="exclusion-list">
                                {items.map(item => (
                                    <label key={item.id} className={`exclusion-item ${excludedItems.has(item.id) ? 'excluded' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={excludedItems.has(item.id)}
                                            onChange={() => toggleExcludeItem(item.id)}
                                        />
                                        <span>{item.name}</span>
                                        <span className="item-price">تكلفة: {formatNumber(item.cost)} | بيع: {formatNumber(item.price)}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button
                                className="save-btn"
                                onClick={applyBulkIncrease}
                                disabled={isApplyingBulk}
                            >
                                {isApplyingBulk ? 'جاري التطبيق...' : 'تطبيق الزيادة'}
                            </button>
                            <button className="cancel-btn" onClick={() => setIsBulkModalOpen(false)}>إلغاء</button>
                        </div>
                    </div>
                </div>
            )}
            <Link to="/sales-home"><button className="back-btn">الرجوع</button></Link>

            <div className="sales-items-container">

                <div className="page-header">

                    <h1>إدارة أصناف المبيعات</h1>
                    <div className="header-actions">
                        <button className="bulk-btn" onClick={openBulkModal}>زيادة عامة</button>
                        <button className="add-btn" onClick={openAddModal}>+ صنف جديد</button>
                    </div>
                </div>

                <div className="search-box">
                    <label>بحث:</label>
                    <input
                        type="text"
                        placeholder="بحث عن صنف..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <div className="content-area">
                    <table className="items-table">
                        <thead>
                            <tr>
                                <th style={{ width: '20%' }}>اسم الصنف</th>
                                <th style={{ width: '10%' }}>المخزون</th>
                                <th style={{ width: '12%' }}>التكلفة</th>
                                <th style={{ width: '12%' }}>سعر البيع</th>
                                <th style={{ width: '12%' }}>نسبة الربح %</th>
                                <th style={{ width: '12%' }}>الربح</th>
                                <th style={{ width: '15%' }}>إجراءات</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.filter(item =>
                                item.name.toLowerCase().includes(searchQuery.toLowerCase())
                            ).map(item => (
                                <tr key={item.id}>
                                    <td>{item.name}</td>
                                    <td>{item.stock}</td>
                                    <td>{formatNumber(item.cost)}</td>
                                    <td>{formatNumber(item.price)}</td>
                                    <td className="calculated-cell">
                                        {item.profitPercentage !== null ? `${Number(item.profitPercentage).toFixed(1)}%` : '-'}
                                    </td>
                                    <td className="calculated-cell">
                                        {calculateProfit(item.cost, item.price)}
                                    </td>
                                    <td>
                                        <button className="action-btn edit-btn" onClick={() => openEditModal(item)}>تعديل</button>
                                        <button className="action-btn" onClick={() => deleteItem(item.id)}>حذف</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
